/**
 * Esta semana — the merchant's card queue (PRODUCT §4.1, §4.3, §4.7). Reads
 * GET /api/goals/queue, the same rule the weekly brief uses, so the cards in the mail are
 * the cards here. Hygiene cards render through HygieneCard (unchanged); plan cards through
 * PlanCard with the three answers: Ya lo cambié · Ahora no · No me interesa.
 *
 * Impersonal voice, voseo. No internals: no lever areas, no origins, no stakes as numbers.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, Text, Tag, Button, Input, Textarea } from '@nimbus-ds/components'
import { goalService, type QueueCard, type QueueResponse, type Actionable, type Declaracion, type DesdeCuando, type ActivationWindow } from '@/api/goals'
import { HygieneCard } from '@/components/merchant/HygieneCard'
import { trackAppEvent } from '@/lib/appTelemetry'

export function fmtDia(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getDate()}/${d.getMonth() + 1}`
}

/** HygieneCard expects an Actionable; a queue card carries exactly the fields it renders. */
function asActionable(c: QueueCard): Actionable {
  return {
    actionable_id: c.actionable_id, store_id: '', goal_id: c.goal_id || '', name: c.name, intent: c.intent,
    type: 'change', status: c.status, activated_at: c.activated_at, indicators: [], evidence: null, provisions: [],
    experiment_id: null, source: 'consultant', hygiene: c.hygiene, created_at: c.created_at || '', updated_at: c.created_at || '',
  } as Actionable
}

/** Where a card acts, in the merchant's words — the pre-filled "dónde" (PRODUCT §2.7). */
const SURFACE_LABEL: Record<string, string> = { home: 'la portada', menu: 'el menú', catalog: 'el catálogo', pdp: 'la ficha de producto', checkout: 'el checkout', landing: 'la página de llegada', search: 'el buscador', cart: 'el carrito' }
const today = () => new Date().toISOString().slice(0, 10)
// "Desde cuándo" manages ambiguity: a merchant rarely knows the exact day, and a wrong date is
// worse than none. Coarse answers, plus the exact day for whoever knows it.
const CUANDO: Array<{ id: DesdeCuando | 'dia'; label: string }> = [
  { id: 'hoy', label: 'Hoy' }, { id: 'esta_semana', label: 'Esta semana' }, { id: 'semana_pasada', label: 'La semana pasada' },
  { id: 'mas_de_dos_semanas', label: 'Hace más de dos semanas' }, { id: 'no_se', label: 'No sé' }, { id: 'dia', label: 'Sé el día exacto' }
]

/** When the change was live, said with the precision the merchant gave. */
export function fmtActivacion(w: ActivationWindow | null | undefined, activatedAt: string | null | undefined): string {
  if (!w || w.precision === 'dia' || !w.precision) return `Aplicado el ${fmtDia(activatedAt)}.`
  if (w.precision === 'semana') return `Aplicado entre el ${fmtDia(w.earliest)} y el ${fmtDia(w.latest)}.`
  if (w.precision === 'antes') return `Aplicado antes del ${fmtDia(w.latest)}.`
  return 'Aplicado; no sabemos desde cuándo.'
}

export function PlanCard({ card, onChange }: { card: QueueCard; onChange: (next: QueueCard | null) => void }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The optional step behind "Ya lo cambié": qué · dónde · desde cuándo, pre-filled from the
  // card. Confirming untouched is the same single tap as before (the defaults get recorded).
  const [step, setStep] = useState(false)
  const [decl, setDecl] = useState<{ que: string; donde: string }>({ que: card.intent || card.name, donde: (card.surface && SURFACE_LABEL[card.surface]) || '' })
  const [cuando, setCuando] = useState<DesdeCuando | 'dia'>('hoy')
  const [dia, setDia] = useState<string>(today())
  const unseen = card.verify !== 'catalog'

  async function run(kind: 'listo' | 'ahora_no' | 'no_me_interesa') {
    setBusy(true); setError(null)
    try {
      if (kind === 'listo') {
        const desde = cuando === 'dia' ? { dia } : { cuando }
        const declaracion: Declaracion = { que: decl.que.trim() || null, donde: decl.donde.trim() || null, desde }
        const edited = declaracion.que !== (card.intent || card.name) || cuando !== 'hoy'
        const saved = await goalService.updateActionable(card.actionable_id, { status: 'active', declaracion })
        trackAppEvent('card_listo', { actionable_id: card.actionable_id, kind: card.kind, edited, cuando, otra_cosa: !!saved?.declaracion?.otra_cosa })
        onChange({ ...card, status: 'active', activated_at: saved?.activated_at || new Date().toISOString(), activation: 'declarada', declaracion: saved?.declaracion || declaracion, activation_window: saved?.activation_window || null })
      } else if (kind === 'ahora_no') {
        await goalService.skipActionable(card.actionable_id)
        trackAppEvent('card_ahora_no', { actionable_id: card.actionable_id, kind: card.kind })
        onChange(null)
      } else {
        await goalService.dismissActionable(card.actionable_id)
        trackAppEvent('card_no_me_interesa', { actionable_id: card.actionable_id, kind: card.kind })
        onChange(null)
      }
    } catch {
      setError('No se pudo guardar. Probá de nuevo en un rato.')
    } finally {
      setBusy(false)
    }
  }

  if (card.status === 'active') {
    return (
      <Card>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Tag appearance="primary">Marcado como hecho</Tag>
            <Text fontWeight="medium">{card.name}</Text>
          </div>
          <Text fontSize="caption" color="neutral-textLow">
            {fmtActivacion(card.activation_window, card.activated_at)} Se lee desde el {fmtDia(card.activated_at)}.{card.activation === 'declarada' ? ' Según lo que nos contaste.' : ''}
          </Text>
          {card.declaracion?.otra_cosa && card.declaracion.que && (
            <Text fontSize="caption" color="neutral-textLow">Lo que hiciste: {card.declaracion.que}</Text>
          )}
        </div>
      </Card>
    )
  }

  if (step) {
    return (
      <Card>
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Tag appearance="primary">Ya lo cambié</Tag>
            <Text fontWeight="medium">{card.name}</Text>
          </div>
          <Text fontSize="caption" color="neutral-textLow">Contanos qué cambiaste. Si fue tal cual la tarjeta, confirmá y listo.</Text>
          <div className="flex flex-col gap-2">
            <Text fontSize="caption" fontWeight="medium">Qué</Text>
            <Textarea id={`decl-que-${card.actionable_id}`} value={decl.que || ''} onChange={(e) => setDecl({ ...decl, que: e.target.value })} lines={2} />
            <Text fontSize="caption" fontWeight="medium">Dónde</Text>
            <Input id={`decl-donde-${card.actionable_id}`} value={decl.donde || ''} onChange={(e) => setDecl({ ...decl, donde: e.target.value })} placeholder="la portada, el menú, una categoría…" />
            <Text fontSize="caption" fontWeight="medium">Desde cuándo</Text>
            <div className="flex flex-wrap gap-2">
              {CUANDO.map((c) => (
                <Button key={c.id} appearance={cuando === c.id ? 'primary' : 'neutral'} onClick={() => setCuando(c.id)} disabled={busy}>{c.label}</Button>
              ))}
            </div>
            {cuando === 'dia' && <Input id={`decl-dia-${card.actionable_id}`} type="date" value={dia} max={today()} onChange={(e) => setDia(e.target.value)} />}
            {(cuando === 'no_se' || cuando === 'mas_de_dos_semanas') && (
              <Text fontSize="caption" color="neutral-textLow">Sin una fecha cierta no comparamos con el antes: se lee lo que pasa de acá en adelante.</Text>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button appearance="primary" onClick={() => run('listo')} disabled={busy}>Confirmar</Button>
            <Button appearance="transparent" onClick={() => setStep(false)} disabled={busy}>Volver</Button>
          </div>
          {error && <Text fontSize="caption" color="danger-textLow">{error}</Text>}
          <Text fontSize="caption" color="neutral-textLow">Si no sabés el día, decilo: una fecha aproximada vale menos que ninguna. Si hiciste otra cosa, se lee igual y queda anotado como lo que hiciste.</Text>
        </div>
      </Card>
    )
  }

  return (
    <Card>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Tag appearance="primary">Esta semana</Tag>
          <Text fontWeight="medium">{card.name}</Text>
          {card.meta_hint && <Text fontSize="caption" color="neutral-textLow">· {card.meta_hint}</Text>}
        </div>
        {card.intent && <Text fontSize="caption" color="neutral-textLow">{card.intent}</Text>}
        {unseen && <Text fontSize="caption" color="neutral-textLow">Este cambio no lo podemos ver desde afuera; leemos lo que nos cuentes.</Text>}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button appearance="primary" onClick={() => setStep(true)} disabled={busy}>Ya lo cambié</Button>
          <Button appearance="neutral" onClick={() => run('ahora_no')} disabled={busy}>Ahora no</Button>
          <Button appearance="transparent" onClick={() => run('no_me_interesa')} disabled={busy}>No me interesa</Button>
        </div>
        {error && <Text fontSize="caption" color="danger-textLow">{error}</Text>}
        <Text fontSize="caption" color="neutral-textLow">Cuando lo hagas, marcalo: desde ese día se mide. «Ahora no» lo guarda para más adelante.</Text>
      </div>
    </Card>
  )
}

export function QueueCardView({ card, onChange }: { card: QueueCard; onChange: (next: QueueCard | null) => void }) {
  if (card.kind === 'hygiene' && card.hygiene) {
    return (
      <HygieneCard
        actionable={asActionable(card)}
        onChange={(a) => onChange(a.status === 'dismissed' ? null : { ...card, status: a.status, activated_at: a.activated_at ?? card.activated_at, hygiene: a.hygiene })}
      />
    )
  }
  return <PlanCard card={card} onChange={onChange} />
}

/** The Inicio section: the week's cards, the WIP message, and a two-line peek at the backlog. */
export function EstaSemanaSection({ queue, onQueue }: { queue: QueueResponse | null; onQueue: (q: QueueResponse) => void }) {
  if (!queue) return null
  const replace = (id: string, next: QueueCard | null) => {
    const shown = next ? queue.shown.map((c) => (c.actionable_id === id ? next : c)) : queue.shown.filter((c) => c.actionable_id !== id)
    onQueue({ ...queue, shown })
  }
  const nothing = queue.shown.length === 0 && queue.reason !== 'wip_full'
  if (nothing && queue.backlog.length === 0 && queue.active.length === 0) return null
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <Text fontWeight="medium">Esta semana</Text>
        <Link to="/merchant/semana" className="text-sm text-[var(--nimbus-colors-primary-textLow)] underline-offset-2 hover:underline">Ver todo</Link>
      </div>
      {queue.reason === 'wip_full' && (
        <Card>
          <Text>Primero leamos lo que ya cambiaste: {queue.wip.active} cambios aplicados esperan su lectura antes de proponer otro.</Text>
        </Card>
      )}
      {queue.shown.map((c) => <QueueCardView key={c.actionable_id} card={c} onChange={(next) => replace(c.actionable_id, next)} />)}
      {nothing && queue.active.length > 0 && (
        <Card><Text fontSize="caption" color="neutral-textLow">Nada nuevo para hacer esta semana. {queue.active.length} cambio{queue.active.length === 1 ? '' : 's'} en lectura.</Text></Card>
      )}
      {queue.backlog.length > 0 && (
        <Text fontSize="caption" color="neutral-textLow">
          Después de estas: {queue.backlog.slice(0, 2).map((c) => c.name).join(' · ')}{queue.backlog.length > 2 ? ` · y ${queue.backlog.length - 2} más` : ''}.
        </Text>
      )}
    </section>
  )
}

/** Load once; failure-silent (the standup renders without the section). */
export function useQueue(storeId: string | null) {
  const [queue, setQueue] = useState<QueueResponse | null>(null)
  useEffect(() => {
    if (!storeId) return
    goalService.getQueue()
      .then((q) => {
        setQueue(q)
        if (q.shown.length) trackAppEvent('queue_viewed', { shown: q.shown.length, backlog: q.backlog.length, wip_full: q.reason === 'wip_full', posture: q.posture })
      })
      .catch(() => {})
  }, [storeId])
  return [queue, setQueue] as const
}
